import React, { createContext, useContext, ReactNode } from "react";

import { ToastProviderWrapper, useToastContext } from "../components/ui/use-toast";

export type AlertType = "success" | "info" | "warning" | "error";

interface NotificationContextProps {
  showNotification: (type: AlertType, message: string, title?: string) => void;
}

const NotificationContext = createContext<NotificationContextProps | undefined>(
  undefined
);

const NotificationBridge = ({ children }: { children: ReactNode }) => {
  const { toast } = useToastContext();

  const showNotification = (type: AlertType, message: string, title?: string) => {
    const variant =
      type === "error"
        ? "destructive"
        : type === "success"
        ? "success"
        : "default";

    toast({
      title: title ?? message,
      description: title ? message : undefined,
      variant,
    });
  };

  return (
    <NotificationContext.Provider value={{ showNotification }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  return (
    <ToastProviderWrapper>
      <NotificationBridge>{children}</NotificationBridge>
    </ToastProviderWrapper>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);

  if (!context) {
    throw new Error(
      "useNotification must be used within a NotificationProvider"
    );
  }

  return context;
};
